import { useMemo, useState } from 'react'
import { CheckCircle, Package, UploadSimple } from '@phosphor-icons/react'
import { Input, Select, TextArea } from '@/ui'
import { useAuth } from '@/auth/context'
import { hasAnyRole, ROLES } from '@/auth/roles'
import { errorMessage } from '../../api/core'
import {
  useApproveLabel,
  useCreateLabel,
  useLabelVersions,
  usePackLots,
  useRetireLabel,
  useSkus,
  type LabelVersion,
} from '../../api/pack'
import { useSites } from '../../api/store'
import { ActionButton } from '../../components/Can'
import { UserName } from '../../components/bits'
import {
  BoardColumns,
  BoardPage,
  BoardTable,
  FactRow,
  Facts,
  LinkButton,
  Note,
  PanelActions,
  Pill,
  SidePanel,
  TwoLine,
  type BoardColumn,
} from '../../components/board'
import { FormDialog } from '../../components/FormDialog'
import { useUrlState } from '../../lib/useUrlState'
import { boardDate } from '../../model/payoutBoard'
import { claimProblems, labelState } from '../../model/packing'
import { BoardCard, BoardField } from './shared'

/**
 * Labels (alignment map V1-F7-04a LabelVersions; F7.4): every label version with its status, who
 * approved it and how many lots carry it; the selected version's legal lines and Approve (quality
 * release role, once per product and hall). Changing text or artwork makes a new version.
 */
export function LabelVersionsPage() {
  const { roles } = useAuth()
  // The packing/allocate route is CENTRAL_ADMIN only (routes.tsx): no button for the others.
  const canAllocate = hasAnyRole(roles, [ROLES.CENTRAL_ADMIN])
  const [f, setF] = useUrlState({ open: '' })
  const labels = useLabelVersions({ limit: 100 })
  const lots = usePackLots({ limit: 200 })
  const rows = labels.data?.rows ?? []
  const lotsBy = useMemo(() => {
    const m = new Map<string, number>()
    for (const l of lots.data?.rows ?? [])
      m.set(l.labelVersionId, (m.get(l.labelVersionId) ?? 0) + 1)
    return m
  }, [lots.data])
  const selected =
    rows.find((l) => l.id === f.open) ?? rows.find((l) => l.status === 'DRAFT') ?? rows[0] ?? null
  const halls = new Set(rows.map((l) => l.premisesCode).filter(Boolean))
  const [uploading, setUploading] = useState(false)

  const replacedBy = (l: LabelVersion) =>
    l.status === 'RETIRED' && l.approvedAt
      ? (rows.find(
          (o) =>
            o.skuId === l.skuId &&
            o.premisesId === l.premisesId &&
            o.status === 'APPROVED' &&
            o.id !== l.id,
        ) ?? null)
      : null

  const columns: BoardColumn<LabelVersion>[] = [
    {
      id: 'version',
      header: 'Version',
      cell: (l) => <TwoLine top={l.code} sub={l.artworkUrl ? 'artwork on file' : 'no artwork'} />,
    },
    { id: 'product', header: 'Product', cell: (l) => <TwoLine top={l.skuName} sub={l.skuCode} /> },
    {
      id: 'status',
      header: 'Status',
      cell: (l) => {
        const r = replacedBy(l)
        const s = labelState(l.status, Boolean(r))
        return <Pill tone={s.tone}>{r ? `Replaced by ${r.code}` : s.text}</Pill>
      },
    },
    {
      id: 'who',
      header: 'Who',
      hideBelow: 'sm',
      cell: (l) =>
        l.approvedById ? (
          <TwoLine
            top={<UserName id={l.approvedById} name={l.approvedByName} />}
            sub={`approved ${boardDate(l.approvedAt)}`}
          />
        ) : (
          <TwoLine top="—" sub={l.createdAt ? `uploaded ${boardDate(l.createdAt)}` : undefined} />
        ),
    },
    { id: 'lots', header: 'Lots', align: 'right', cell: (l) => lotsBy.get(l.id) ?? '—' },
  ]

  return (
    <BoardPage
      title="Labels"
      actions={
        <>
          <ActionButton
            perm="label-versions:create"
            hide
            variant="secondary"
            leadingIcon={<UploadSimple />}
            onClick={() => setUploading(true)}
          >
            Upload a new version
          </ActionButton>
          {canAllocate && (
            <LinkButton to="/c/packing/allocate" icon={<Package aria-hidden="true" />}>
              Allocate a pack run
            </LinkButton>
          )}
        </>
      }
    >
      <BoardColumns
        main={
          <>
            {halls.size > 0 && <Note>Hall {[...halls].join(', ')}</Note>}
            <BoardTable<LabelVersion>
              caption="Label versions"
              columns={columns}
              rows={rows}
              getRowId={(l) => l.id}
              selectedId={selected?.id ?? null}
              onRowClick={(l) => setF({ open: l.id })}
              loading={labels.isLoading}
              error={labels.error ? errorMessage(labels.error) : undefined}
              empty="No label versions yet."
            />
            <BoardCard title="Rules for every label">
              <ul className="flex list-disc flex-col gap-1 pl-5 text-[14px]">
                <li>
                  One approved version per product and hall. A product cannot be allocated to a pack
                  run until its label is approved (F7.1).
                </li>
                <li>
                  Changing text or artwork makes a new version that needs approval. Lots already
                  packed keep the version they were printed with.
                </li>
                <li>
                  The claim must say <i>tested, not detected</i> and give the LOQ. A claim without
                  the LOQ cannot be approved; never “residue-free”.
                </li>
                <li>
                  QR shape is fixed for good:{' '}
                  <span className="font-mono text-[13px]">
                    https://id.uncompromised.in/&#123;product number&#125;/&#123;pack lot code&#125;
                  </span>
                </li>
              </ul>
            </BoardCard>
          </>
        }
        side={selected ? <LabelPanel l={selected} /> : undefined}
      />
      <UploadDialog open={uploading} onClose={() => setUploading(false)} />
    </BoardPage>
  )
}

function LabelPanel({ l }: { l: LabelVersion }) {
  const approve = useApproveLabel()
  const retire = useRetireLabel()
  const s = labelState(l.status, false)
  const problems = [
    ...(!l.mrpInr ? ['No MRP'] : []),
    ...(!l.allergen ? ['No allergen line'] : []),
    ...(!l.licenceNo ? ['The hall has no FSSAI licence on file'] : []),
    ...claimProblems(l.claimText, l.claimMaxLoq),
  ]
  const loqOk = !claimProblems(l.claimText, l.claimMaxLoq).some((p) => /LOQ/.test(p))
  return (
    <SidePanel
      label="Selected label version"
      title={l.code}
      sub={`${l.skuName} · ${l.premisesCode ?? ''}${l.premisesName ? ` · ${l.premisesName}` : ''}`}
    >
      <Pill tone={s.tone}>{s.text}</Pill>
      {l.artworkUrl && (
        <a
          href={l.artworkUrl}
          target="_blank"
          rel="noreferrer"
          className="text-[14px] font-bold underline"
        >
          Open the artwork
        </a>
      )}
      <Facts>
        <FactRow label="Product name">{l.skuName}</FactRow>
        <FactRow label="Product">{l.skuCode}</FactRow>
        <FactRow label="MRP">{l.mrpInr ? `₹${l.mrpInr}` : '—'}</FactRow>
        <FactRow label="FSSAI licence">{l.licenceNo ?? '—'}</FactRow>
        <FactRow label="Hall">{l.premisesCode ?? '—'}</FactRow>
      </Facts>
      <div className="flex flex-col gap-1 text-[14px]">
        <b>Allergen line</b>
        <span>{l.allergen ?? '—'}</span>
        <b className="mt-1">Claim text</b>
        <span>{l.claimText ?? '—'}</span>
        <span className="text-muted">
          Panel {l.claimPanel ?? '—'} · at least {l.claimMinAnalytes ?? '—'} analytes · LOQ{' '}
          {l.claimMaxLoq ?? '—'} mg/kg
        </span>
      </div>
      {l.status === 'DRAFT' &&
        (loqOk ? (
          <Pill tone="ok">Claim gives the LOQ</Pill>
        ) : (
          <Pill tone="bad">Claim does not give the LOQ</Pill>
        ))}
      {l.status === 'DRAFT' && problems.length > 0 && (
        <div className="flex flex-col gap-0.5">
          {problems.map((p) => (
            <span key={p} className="text-[13px] text-danger">
              {p}
            </span>
          ))}
        </div>
      )}
      {l.status === 'APPROVED' && l.approvedById && (
        <Note>
          Approved by <UserName id={l.approvedById} name={l.approvedByName} /> on{' '}
          {boardDate(l.approvedAt)}.
        </Note>
      )}
      <div className="flex-1" />
      {l.status === 'DRAFT' && (
        <>
          <Note>
            Approving retires the product’s earlier approved version at this hall. The server
            re-checks every legal line.
          </Note>
          <PanelActions>
            <ActionButton
              perm="label-versions:approve"
              leadingIcon={<CheckCircle />}
              loading={approve.isPending}
              blockedReason={problems.length ? 'Fix the lines in red first.' : null}
              onClick={() => void approve.mutateAsync(l.id).catch(() => undefined)}
            >
              Approve
            </ActionButton>
            <ActionButton
              perm="label-versions:update"
              hide
              variant="secondary"
              loading={retire.isPending}
              onClick={() => void retire.mutateAsync(l.id).catch(() => undefined)}
            >
              Withdraw this draft
            </ActionButton>
          </PanelActions>
        </>
      )}
    </SidePanel>
  )
}

function UploadDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const skus = useSkus()
  const sites = useSites()
  const halls = (sites.data ?? []).filter((s) => s.kind === 'PACKING')
  const create = useCreateLabel()
  const [v, setV] = useState({
    skuId: '',
    code: '',
    hallId: '',
    mrp: '',
    allergen: '',
    claim: '',
    panel: 'PESTICIDES',
    minAnalytes: '',
    maxLoq: '0.01',
    artwork: '',
  })
  const set = (k: keyof typeof v) => (val: string) => setV((x) => ({ ...x, [k]: val }))
  const hallId = v.hallId || halls[0]?.id || ''
  const problems = claimProblems(v.claim, v.maxLoq)
  const ok = v.skuId && v.code.trim() && hallId && /^\d+(\.\d{1,2})?$/.test(v.mrp.trim())
  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title="Upload a new label version"
      description="It is saved as a draft. A quality person who did not write it approves it."
      saveLabel="Save draft"
      loading={create.isPending}
      canSave={Boolean(ok)}
      onSave={() =>
        create.mutateAsync({
          skuId: v.skuId,
          code: v.code.trim(),
          premisesLocationId: hallId,
          mrpInr: v.mrp.trim(),
          allergenStatement: v.allergen.trim() || undefined,
          claimText: v.claim.trim() || undefined,
          claimPanel: v.panel.trim() || undefined,
          claimMinAnalytes: v.minAnalytes ? Number(v.minAnalytes) : undefined,
          claimMaxLoq: v.maxLoq.trim() || undefined,
          artworkUrl: v.artwork.trim() || undefined,
        })
      }
    >
      <BoardField label="Product">
        <Select
          value={v.skuId}
          onChange={set('skuId')}
          placeholder="Pick a product"
          options={(skus.data ?? []).map((s) => ({
            value: s.id,
            label: `${s.name} · ${s.netG} g (${s.code})`,
          }))}
        />
      </BoardField>
      <BoardField label="Version code" hint="e.g. GN-500-L2">
        <Input value={v.code} onChange={(e) => set('code')(e.target.value)} />
      </BoardField>
      <BoardField label="Hall">
        <Select
          value={hallId}
          onChange={set('hallId')}
          options={halls.map((h) => ({ value: h.id, label: `${h.code} · ${h.name}` }))}
        />
      </BoardField>
      <BoardField label="MRP (₹)">
        <Input inputMode="decimal" value={v.mrp} onChange={(e) => set('mrp')(e.target.value)} />
      </BoardField>
      <BoardField label="Allergen line">
        <TextArea rows={2} value={v.allergen} onChange={(e) => set('allergen')(e.target.value)} />
      </BoardField>
      <BoardField
        label="Claim text"
        error={v.claim && problems.length ? problems.join(' · ') : undefined}
      >
        <TextArea
          rows={2}
          value={v.claim}
          onChange={(e) => set('claim')(e.target.value)}
          placeholder="Tested for N pesticides: not detected (LOQ 0.01 mg/kg)."
        />
      </BoardField>
      <div className="grid grid-cols-3 gap-2">
        <BoardField label="Claim panel">
          <Input value={v.panel} onChange={(e) => set('panel')(e.target.value)} />
        </BoardField>
        <BoardField label="Analytes, at least">
          <Input
            inputMode="numeric"
            value={v.minAnalytes}
            onChange={(e) => set('minAnalytes')(e.target.value.replace(/\D/g, ''))}
          />
        </BoardField>
        <BoardField label="LOQ (mg/kg)">
          <Input
            inputMode="decimal"
            value={v.maxLoq}
            onChange={(e) => set('maxLoq')(e.target.value)}
          />
        </BoardField>
      </div>
      <BoardField
        label="Artwork link"
        hint="Link to the artwork PDF (the media store takes photos only)."
      >
        <Input value={v.artwork} onChange={(e) => set('artwork')(e.target.value)} />
      </BoardField>
    </FormDialog>
  )
}
